/**
 * The time-in-lieu balance beside holiday (DESIGN-toil.md screen 2, decision J): what's left
 * to use, anything waiting for sign-off, the soonest use-by date, and where it comes from.
 * Shown while TOIL is on, or while the person still has a history (switching TOIL off
 * deletes nothing — assumption 6). Renders nothing otherwise.
 *
 * Always hours and minutes (C), even for somebody whose holiday is in days.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Plus, History } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toilApi } from '../../lib/api';
import { formatDateGB } from '../../lib/dates';
import { useAuth } from '../../context/AuthContext';
import ClaimToilDialog from './ClaimToilDialog';
import ToilHistory from './ToilHistory';

export default function ToilCard({ className = '', refreshKey, wide = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(() => {
    toilApi.mine().then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data || (!data.enabled && !data.summary?.ever)) return null;
  const s = data.summary || {};
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const withdraw = async (id) => {
    try {
      await toilApi.withdraw(id);
      toast.success('Claim withdrawn');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not withdraw the claim');
    }
  };

  return (
    <div className={`card-hive p-5 ${className}`} data-testid="toil-card">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="flex items-center gap-2 text-sm text-muted-foreground font-semibold">
          <Clock className="w-4 h-4 text-hive-orange" /> Time in lieu to use
        </span>
        <span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-secondary text-muted-foreground">TOIL</span>
      </div>
      <div className={wide ? 'grid gap-x-10 gap-y-2 md:grid-cols-2' : ''}>
      <div>
      <p className={`mt-2 text-[32px] leading-tight font-heading font-bold ${s.to_use_minutes < 0 ? 'text-red-600' : ''}`} data-testid="toil-to-use">{s.to_use}</p>
      {s.waiting_claims > 0 && (
        <p className="text-sm mt-1.5 flex items-center gap-2">
          <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-amber-500/15 text-amber-600">Waiting</span>
          {s.waiting} claimed, waiting for sign-off
        </p>
      )}
      {s.soonest && (
        <p className="text-sm mt-1.5 flex items-center gap-2">
          <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-red-500/15 text-red-600">Use by {formatDateGB(s.soonest.use_by)}</span>
          {s.soonest.amount}
        </p>
      )}
      </div>
      {(s.by_expiry || []).length > 1 && (
        <div className={`${wide ? 'md:mt-0' : ''} mt-3 text-xs text-muted-foreground space-y-0.5`}>
          {s.by_expiry.map((g) => (
            <div key={g.use_by || 'never'} className="flex justify-between">
              <span>{g.use_by ? `Use by ${formatDateGB(g.use_by)}` : "Doesn't expire"}</span>
              <span className="font-medium text-foreground">{g.amount}</span>
            </div>
          ))}
          <p className="pt-1">Bookings use the soonest-expiring time first.</p>
        </div>
      )}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {data.enabled && (
          <Button type="button" size="sm" className="btn-primary" onClick={() => setClaimOpen(true)} data-testid="toil-claim-btn">
            <Plus className="w-4 h-4 mr-1" /> Claim time in lieu
          </Button>
        )}
        <Button type="button" size="sm" variant="outline" onClick={() => setHistoryOpen(true)} data-testid="toil-history-btn">
          <History className="w-4 h-4 mr-1" /> History
        </Button>
      </div>

      <ClaimToilDialog open={claimOpen} onOpenChange={setClaimOpen} onDone={load}
        withRate={isAdmin} usualRate={data.settings?.default_rate} />

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-[820px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Time in lieu history</DialogTitle>
          </DialogHeader>
          <ToilHistory lines={data.history} onWithdraw={withdraw} withdrawableBy />
        </DialogContent>
      </Dialog>
    </div>
  );
}
